import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  Package,
  User,
  Search,
  Calendar,
  X,
  Briefcase,
  Truck,
  Printer,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { fetchEntregasClientes } from '@/services/entregasClientes.service';
import { getClientGarments } from '@/pages/parametrizacion/clientGarments/Services/clientGarments.services';
import { getPrinterInstance } from '@/utils/bluetoothPrinter';

const EntregasCompletadas = ({ idLavanderia, onCountChange }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [entregas, setEntregas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntrega, setSelectedEntrega] = useState(null);

  useEffect(() => {
    loadEntregas();
  }, [idLavanderia]);

  const loadEntregas = async () => {
    setIsLoading(true);
    try {
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);

      const query = {
        'lavanderia.id': idLavanderia,
        tipo: 'Entrega',
        estado: 'Entregado',
        fecha_entrega: { $gte: hoy.toISOString() },
      };

      const rolLower = String(user?.role ?? user?.rol ?? '').toLowerCase();
      if (rolLower === 'conductor') {
        query['conductor.id'] = user?.id ?? user?._id;
      }

      const response = await fetchEntregasClientes({ query, limit: 100, page: 1 });
      setEntregas(response.data || []);
      onCountChange?.(response.totalRecords || 0);
    } catch (error) {
      console.error('Error al cargar entregas completadas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las entregas completadas',
        variant: 'destructive',
      });
      setEntregas([]);
      onCountChange?.(0);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredEntregas = entregas.filter((e) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      e.cliente?.nombre?.toLowerCase().includes(term) ||
      e.consecutivo?.toLowerCase().includes(term) ||
      e.conductor?.nombre?.toLowerCase().includes(term)
    );
  });

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto" />
        <p className="text-sm text-gray-500 mt-4">Cargando entregas completadas...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Búsqueda */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar por cliente, consecutivo o conductor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-sm h-10"
              />
            </div>
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {filteredEntregas.length} entregada(s) hoy
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      {filteredEntregas.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No hay entregas completadas hoy</h3>
            <p className="text-sm text-gray-500">Las entregas confirmadas aparecerán aquí</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2">
          {filteredEntregas.map((entrega) => {
            const totalConfirmadas = entrega.items?.reduce((s, i) => s + (i.cantidad_confirmada || 0), 0) || 0;
            const totalAlistadas = entrega.items?.reduce((s, i) => s + (i.cantidad_alistada || 0), 0) || 0;
            const hasDiff = totalAlistadas !== totalConfirmadas;

            return (
              <motion.div key={entrega._id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <Card
                  className="border-l-4 border-l-green-400 cursor-pointer hover:shadow-md active:scale-[0.98] transition-all"
                  onClick={() => setSelectedEntrega(entrega)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-green-700">{entrega.consecutivo}</span>
                      <Badge className="bg-green-100 text-green-800 text-xs">Entregado</Badge>
                      {hasDiff && <Badge className="bg-orange-100 text-orange-800 text-xs">Ajustado</Badge>}
                    </div>
                    <p className="font-medium text-sm text-gray-900 truncate">{entrega.cliente?.nombre}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Package className="h-3 w-3" />{totalConfirmadas} prendas
                      </span>
                      {entrega.totalBolsas > 0 && (
                        <span className="flex items-center gap-1">
                          <Briefcase className="h-3 w-3" />{entrega.totalBolsas} bolsa(s)
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />{formatTime(entrega.fecha_entrega)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Truck className="h-3 w-3" />{entrega.conductor?.nombre || 'Sin conductor'}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Modal detalle fullscreen */}
      <Dialog open={!!selectedEntrega} onOpenChange={() => setSelectedEntrega(null)}>
        <DialogContent className="!fixed !inset-0 !left-0 !top-0 !translate-x-0 !translate-y-0 !max-w-none !w-full !h-full !rounded-none !p-0 !gap-0 !border-0 flex flex-col [&>button]:hidden">
          <DialogTitle className="sr-only">Detalle de entrega</DialogTitle>
          {selectedEntrega && (
            <EntregaDetailView
              entrega={selectedEntrega}
              onClose={() => setSelectedEntrega(null)}
              formatDateTime={formatDateTime}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EntregaDetailView = ({ entrega, onClose, formatDateTime }) => {
  const { toast } = useToast();
  const [isPrinting, setIsPrinting] = useState(false);
  const printer = getPrinterInstance();

  const totalAlistadas = entrega.items?.reduce((s, i) => s + (i.cantidad_alistada || 0), 0) || 0;
  const totalConfirmadas = entrega.items?.reduce((s, i) => s + (i.cantidad_confirmada || 0), 0) || 0;
  const hasDiff = totalAlistadas !== totalConfirmadas;

  const handlePrint = async () => {
    if (!printer.isBluetoothAvailable()) {
      toast({ title: 'No disponible', description: 'Tu navegador no soporta Bluetooth', variant: 'destructive' });
      return;
    }
    setIsPrinting(true);
    try {
      if (!printer.isConnected()) {
        toast({ title: 'Conectando...', description: 'Selecciona tu impresora Bluetooth' });
        await printer.connect();
      }
      toast({ title: 'Imprimiendo...', description: 'Generando 2 copias' });

      // Enriquecer items con familia para impresión agrupada
      let familiaMap = new Map();
      try {
        const res = await getClientGarments({ limit: 500 });
        (res.data || []).forEach((g) => familiaMap.set(String(g._id), g.familia || null));
      } catch { /* best-effort */ }

      const itemsEnriched = (entrega.items || []).map((item) => ({
        ...item,
        familia: familiaMap.get(String(item.prendaId)) || null,
      }));

      await printer.printEntrega({ ...entrega, items: itemsEnriched });
      toast({ title: 'Listo', description: `Etiquetas ${entrega.consecutivo} impresas` });
    } catch (error) {
      const raw = (error?.message || '').toLowerCase();
      const msg = raw.includes('user cancelled') || raw.includes('user denied')
        ? 'Impresión cancelada.'
        : raw.includes('no se pudo conectar') || raw.includes('no hay conexión')
        ? 'No se pudo conectar. Verifica que la impresora esté encendida y cerca.'
        : error.message || 'No se pudo imprimir. Intenta de nuevo.';
      toast({ title: 'Error de impresión', description: msg, variant: 'destructive' });
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-green-600 text-white p-4 flex items-center justify-between shrink-0">
        <div>
          <h2 className="text-lg font-bold">{entrega.consecutivo}</h2>
          <p className="text-green-100 text-sm">{entrega.cliente?.nombre}</p>
        </div>
        <Button size="icon" variant="ghost" className="text-white hover:bg-green-700 h-8 w-8" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Content scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Info general */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Estado</p>
            <div className="flex gap-1 flex-wrap">
              <Badge className="bg-green-100 text-green-800">Entregado</Badge>
              {hasDiff && <Badge className="bg-orange-100 text-orange-800">Ajustado</Badge>}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Lavandería</p>
            <p className="text-sm font-medium">{entrega.lavanderia?.nombre || '-'}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Conductor</p>
            <div className="flex items-center gap-1">
              <Truck className="h-3.5 w-3.5 text-gray-400" />
              <p className="text-sm font-medium">{entrega.conductor?.nombre || '-'}</p>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Recibió</p>
            <div className="flex items-center gap-1">
              <User className="h-3.5 w-3.5 text-gray-400" />
              <p className="text-sm font-medium">{entrega.recibe?.nombre || '-'}</p>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Fecha envío</p>
            <p className="text-sm font-medium">{formatDateTime(entrega.fecha_envio)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Fecha entrega</p>
            <p className="text-sm font-medium">{formatDateTime(entrega.fecha_entrega)}</p>
          </div>
        </div>

        {/* Resumen */}
        <div className="flex items-center gap-4 bg-green-50 rounded-lg p-3">
          <div className="text-center">
            <p className="text-2xl font-bold text-green-700">{totalConfirmadas}</p>
            <p className="text-xs text-green-600">Confirmadas</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-500">{totalAlistadas}</p>
            <p className="text-xs text-gray-500">Alistadas</p>
          </div>
          {entrega.totalBolsas > 0 && (
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{entrega.totalBolsas}</p>
              <p className="text-xs text-blue-600">Bolsa(s)</p>
            </div>
          )}
          {hasDiff && (
            <div className="text-center">
              <p className={`text-2xl font-bold ${totalConfirmadas - totalAlistadas > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                {totalConfirmadas - totalAlistadas > 0 ? '+' : ''}{totalConfirmadas - totalAlistadas}
              </p>
              <p className="text-xs text-orange-600">Diferencia</p>
            </div>
          )}
        </div>

        {/* Items */}
        <div>
          <h3 className="font-semibold text-sm text-gray-700 mb-2">
            Detalle de prendas ({(entrega.items || []).length})
          </h3>
          <div className="space-y-1">
            {(entrega.items || []).map((item, idx) => {
              const diff = (item.cantidad_confirmada || 0) - (item.cantidad_alistada || 0);
              return (
                <div key={idx} className="flex items-center justify-between text-sm p-2.5 bg-gray-50 rounded-lg">
                  <span className="truncate flex-1 font-medium">{item.nombre}</span>
                  <div className="flex items-center gap-3 shrink-0 ml-2">
                    <span className="text-gray-400 text-xs">Alistada: {item.cantidad_alistada}</span>
                    <span className={`font-semibold ${diff !== 0 ? 'text-orange-600' : 'text-green-600'}`}>
                      {item.cantidad_confirmada || 0}
                    </span>
                    {diff !== 0 && (
                      <Badge variant="outline" className={`text-xs ${diff > 0 ? 'text-blue-600 border-blue-300' : 'text-red-600 border-red-300'}`}>
                        {diff > 0 ? '+' : ''}{diff}
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {entrega.observaciones && (
          <div className="bg-yellow-50 rounded-lg p-3">
            <p className="text-xs text-yellow-700 font-semibold mb-1">Observaciones</p>
            <p className="text-sm text-yellow-800">{entrega.observaciones}</p>
          </div>
        )}

        {entrega.alistamientoConsecutivo && (
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs text-blue-700 font-semibold mb-1">Alistamiento origen</p>
            <p className="text-sm text-blue-800">{entrega.alistamientoConsecutivo}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t bg-white shrink-0">
        <Button
          onClick={handlePrint}
          disabled={isPrinting}
          className="w-full gap-2 bg-green-600 hover:bg-green-700 h-11"
        >
          {isPrinting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Imprimiendo...
            </>
          ) : (
            <>
              <Printer className="h-4 w-4" />
              Imprimir Etiquetas (2 copias)
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default EntregasCompletadas;
